// File: src/pages/ImageTestPage.tsx

import React, { useState, useMemo, useEffect } from 'react'; // <-- Добавили useEffect
import { useQuery } from '@tanstack/react-query';
import {
    Box, Button, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Typography, CircularProgress,
    Alert, TextField, Chip, Dialog, DialogTitle, DialogContent, IconButton, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Paper // <-- Добавлен Paper
} from '@mui/material';
import { Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Search as SearchIcon } from '@mui/icons-material';

import {
    Product,
    AIService,
    PromptTemplate,
    ImageGeneration,
} from '../types';

import {
    getProducts,
    fetchAIServices,
    getPromptTemplates,
    getGenerationsForProduct,
} from '../services/api';

import PromptViewerDialog from '../components/PromptViewerDialog';
import AIGuideDialog from '../components/AIGuideDialog';
import ImageGenerationHistoryDialog from '../components/image_gen/ImageGenerationHistoryDialog';
import { useImageGeneration } from '../hooks/useImageGeneration';

const ImageTestPage: React.FC = () => {
    // --- Состояния UI ---
    const [selectedReferenceImageUrl, setSelectedReferenceImageUrl] = useState<string | null>(null);
    const [selectedProductId, setSelectedProductId] = useState<number | ''>('');
    const [selectedAIServiceCode, setSelectedAIServiceCode] = useState<string>('');
    const [selectedImageServiceCode, setSelectedImageServiceCode] = useState<string>('');
    const [selectedPromptTemplateId, setSelectedPromptTemplateId] = useState<number | ''>('');
    const [numImages, setNumImages] = useState<number>(1);
    const [additionalInstructions, setAdditionalInstructions] = useState<string>('');

    const [historyOpen, setHistoryOpen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(null);
    const [isAIGuideOpen, setIsAIGuideOpen] = useState(false);
    const [viewedAIService, setViewedAIService] = useState<AIService | null>(null);
    const [isPromptViewerOpen, setIsPromptViewerOpen] = useState(false);
    const [viewedPromptTemplate, setViewedPromptTemplate] = useState<PromptTemplate | null>(null);
    const [promptsExpanded, setPromptsExpanded] = useState<boolean>(false);

    // --- Запросы к API ---
    const { data: products } = useQuery<Product[]>({ queryKey: ['products'], queryFn: getProducts });
    const { data: allAiServices } = useQuery<AIService[]>({ queryKey: ['aiServices'], queryFn: () => fetchAIServices() });
    const { data: promptTemplates } = useQuery<PromptTemplate[]>({ queryKey: ['promptTemplates', 'image_generation'], queryFn: () => getPromptTemplates('image_generation') });
    const { data: historyGenerations = [], isLoading: isLoadingHistory } = useQuery({
        queryKey: ['generationHistory', selectedProductId],
        queryFn: () => getGenerationsForProduct(selectedProductId as number),
        enabled: !!selectedProductId,
    });

    // --- Кастомный хук для логики генерации ---
    const {
        generationId,
        generationStep,
        generatedPrompts,
        generatedImages,
        error,
        selectedPromptTemplate,
        selectedAIService,
        selectedImageService,
        promptGenMutation,
        imageGenMutation,
        resetGeneration,
        handleLoadGeneration,
        canStartPromptGen,
        canStartImageGen,
        aiRawPrompt,
        aiRawResponse,
    } = useImageGeneration({
        selectedProductId,
        selectedAIServiceCode,
        selectedImageServiceCode,
        selectedPromptTemplateId,
        numImages,
        additionalInstructions,
        selectedReferenceImageUrl,
        allAiServices,
        promptTemplates,
    });

    // --- Фильтрация сервисов ---
    const promptServices = useMemo(() => allAiServices?.filter(s => s.supported_tasks?.includes('prompt_generation')) || [], [allAiServices]);
    const imageServices = useMemo(() => allAiServices?.filter(s => s.supported_tasks?.includes('image_generation')) || [], [allAiServices]);

    const selectedProduct = useMemo(() => {
        return products?.find(p => p.id === selectedProductId);
    }, [products, selectedProductId]);

    // ✅ ДОБАВЛЕНИЕ ЛОГИКИ АВТОЗАПОЛНЕНИЯ
    useEffect(() => {
        if (promptTemplates?.length === 1 && selectedPromptTemplateId === '') {
            setSelectedPromptTemplateId(promptTemplates[0].id);
        }
    }, [promptTemplates, selectedPromptTemplateId]);

    useEffect(() => {
        if (promptServices?.length === 1 && selectedAIServiceCode === '') {
            setSelectedAIServiceCode(promptServices[0].code);
        }
    }, [promptServices, selectedAIServiceCode]);

    useEffect(() => {
        if (imageServices?.length === 1 && selectedImageServiceCode === '') {
            setSelectedImageServiceCode(imageServices[0].code);
        }
    }, [imageServices, selectedImageServiceCode]);

    // --- Обработчики событий ---
    const resetAll = () => {
        // Reset ALL state, including selections
        setSelectedProductId('');
        setSelectedAIServiceCode('');
        setSelectedImageServiceCode('');
        setSelectedPromptTemplateId('');
        setNumImages(1);
        setAdditionalInstructions('');
        setSelectedReferenceImageUrl(null);
        resetGeneration();
    };

    const handleHistoryLoad = (gen: ImageGeneration) => {
        setHistoryOpen(false);
        handleLoadGeneration(gen);
        setSelectedReferenceImageUrl(gen.reference_image_url || null);
        setSelectedProductId(gen.product_id);
    };

    const openFullscreenImage = (url: string) => {
        setFullscreenImageUrl(url);
    };

    const closeFullscreenImage = () => {
        setFullscreenImageUrl(null);
    };

    const handleViewAIService = (service: AIService | null) => {
        setViewedAIService(service);
        setIsAIGuideOpen(true);
    };

    const handleViewPromptTemplate = (template: PromptTemplate | null) => {
        setViewedPromptTemplate(template);
        setIsPromptViewerOpen(true);
    };

    const downloadImage = (url: string, filename: string) => {
        if (!url) {
            console.error("Attempted to download an image with no URL.");
            return;
        }
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadAllImages = () => {
        if (generatedImages.length === 0) {
            console.warn("No images to download.");
            return;
        }
        generatedImages.forEach((img, index) => {
            if (img.status === 'completed' && img.url) {
                const productNameSlug = selectedProduct?.name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/__+/g, '_') || 'generated_image';
                const filename = `${productNameSlug}_gen_${generationId}_img_${index + 1}.jpg`;
                downloadImage(img.url, filename);
            }
        });
    };

    const hasCompletedImages = generatedImages.some(img => img.status === 'completed');

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Image-to-Image Generation
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Секция 1: Выбор продукта и референсного изображения */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                1. Product & Reference Image
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Select product</InputLabel>
                                <Select
                                    value={selectedProductId}
                                    label="Select product"
                                    onChange={e => {
                                        setSelectedProductId(e.target.value as number);
                                        resetGeneration();
                                    }}
                                >
                                    {products?.map((p: Product) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.name} {p.brand ? `(${p.brand})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {selectedProduct && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Select Reference Image:
                                    </Typography>
                                    {(selectedProduct.images && selectedProduct.images.length > 0) ? (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 150, overflowY: 'auto', p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                            {selectedProduct.images.map((url) => (
                                                <Box
                                                    key={url}
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        cursor: 'pointer',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        border: `2px solid ${url === selectedReferenceImageUrl ? '#1976d2' : 'transparent'}`,
                                                        '&:hover': {
                                                            borderColor: url === selectedReferenceImageUrl ? '#1976d2' : '#90caf9',
                                                        },
                                                        transition: 'border-color 0.2s',
                                                    }}
                                                    onClick={() => setSelectedReferenceImageUrl(url)}
                                                >
                                                    <img
                                                        src={url}
                                                        alt="Product reference"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography color="text.secondary" variant="body2">
                                            This product has no images. Please upload images to the product first.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {selectedProductId && (
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                    onClick={() => setHistoryOpen(true)}
                                    size="small"
                                    disabled={isLoadingHistory}
                                >
                                    View History ({historyGenerations.length})
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Секция 2: Настройки AI и шаблона */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                2. Settings
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>AI Service for Prompt Generation</InputLabel>
                                    <Select
                                        value={selectedAIServiceCode}
                                        label="AI Service for Prompt Generation"
                                        onChange={e => setSelectedAIServiceCode(e.target.value)}
                                        disabled={generationStep !== 'idle'}
                                    >
                                        {promptServices?.map((s: AIService) => (
                                            <MenuItem key={s.id} value={s.code}>
                                                {s.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedAIService && (
                                    <Tooltip title="View AI Service Details">
                                        <IconButton onClick={() => handleViewAIService(selectedAIService)} disabled={generationStep !== 'idle'}>
                                            <SearchIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>AI Service for Image Generation</InputLabel>
                                    <Select
                                        value={selectedImageServiceCode}
                                        label="AI Service for Image Generation"
                                        onChange={e => setSelectedImageServiceCode(e.target.value)}
                                        disabled={generationStep !== 'idle'}
                                    >
                                        {imageServices?.map((s: AIService) => (
                                            <MenuItem key={s.id} value={s.code}>
                                                {s.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedImageService && (
                                    <Tooltip title="View AI Service Details">
                                        <IconButton onClick={() => handleViewAIService(selectedImageService)} disabled={generationStep !== 'idle'}>
                                            <SearchIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Prompt Template</InputLabel>
                                    <Select
                                        value={selectedPromptTemplateId}
                                        label="Prompt Template"
                                        onChange={e => setSelectedPromptTemplateId(e.target.value as number)}
                                        disabled={generationStep !== 'idle'}
                                    >
                                        {promptTemplates?.map((t: PromptTemplate) => (
                                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedPromptTemplate && (
                                    <Tooltip title="View Prompt Template">
                                        <IconButton onClick={() => handleViewPromptTemplate(selectedPromptTemplate)} disabled={generationStep !== 'idle'}>
                                            <SearchIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Num Images</InputLabel>
                                        <Select
                                            value={numImages}
                                            label="Num Images"
                                            onChange={e => setNumImages(Number(e.target.value))}
                                            disabled={generationStep !== 'idle'}
                                        >
                                            {[1, 2, 3].map(n => (
                                                <MenuItem key={n} value={n}>{n}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        multiline
                                        minRows={1}
                                        maxRows={3}
                                        fullWidth
                                        label="Additional Instructions (optional)"
                                        value={additionalInstructions}
                                        onChange={e => setAdditionalInstructions(e.target.value)}
                                        disabled={true}
                                        // disabled={generationStep !== 'idle'}
                                        placeholder="e.g. bright colors, studio lighting, no text"
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={() => promptGenMutation.mutate()}
                                    disabled={!canStartPromptGen || promptGenMutation.isLoading}
                                >
                                    {promptGenMutation.isLoading || generationStep === 'prompt_generating'
                                        ? <CircularProgress size={24} />
                                        : 'Generate Prompts'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => imageGenMutation.mutate()}
                                    disabled={!canStartImageGen || imageGenMutation.isLoading}
                                >
                                    {imageGenMutation.isLoading || generationStep === 'image_generating'
                                        ? <CircularProgress size={24} />
                                        : 'Generate Images'}
                                </Button>
                                <Button variant="text" color="secondary" onClick={resetAll}>
                                    Reset
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {(generationStep === 'prompts_ready' || generationStep === 'image_generating' || generationStep === 'completed') && (
                <Accordion expanded={promptsExpanded} onChange={() => setPromptsExpanded(!promptsExpanded)} sx={{ mb: 3}}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography variant="h6">
                            Generated Prompts ({generatedPrompts.length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {generatedPrompts.length === 0 && <Typography>No prompts available.</Typography>}
                        {generatedPrompts.map((prompt: string, i: number) => (
                            <Box
                                key={i}
                                sx={{
                                    p: 1, mb: 1,
                                    border: '1px dashed',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                {prompt}
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}

            {(generationStep === 'image_generating' || generationStep === 'completed') && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Generated Images
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={downloadAllImages}
                                disabled={!hasCompletedImages}
                            >
                                Download All
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            {generatedImages.map((img, idx) => (
                                <Grid key={idx} item xs={12} sm={4}>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            pt: '100%',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: img.status === 'failed' ? 'error.main' : 'divider',
                                            bgcolor: img.status === 'failed' ? '#ffe6e6' : 'background.paper',
                                            cursor: img.status === 'completed' ? 'zoom-in' : 'default',
                                            '&:hover img': {
                                                transform: img.status === 'completed' ? 'scale(1.05)' : 'none',
                                            },
                                            transition: 'transform 0.3s ease-in-out',
                                        }}
                                        onClick={() => img.status === 'completed' && openFullscreenImage(img.url)}
                                    >
                                        {img.status === 'completed' && (
                                            <>
                                                <img
                                                    src={img.url}
                                                    alt={`Generated #${idx + 1}`}
                                                    loading="lazy"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.3s ease-in-out',
                                                    }}
                                                />
                                                <Tooltip title="Download Image">
                                                    <IconButton
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: 8,
                                                            right: 8,
                                                            color: 'white',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                            },
                                                            zIndex: 2,
                                                            opacity: 0,
                                                            transition: 'opacity 0.3s ease-in-out',
                                                            '.MuiBox-root:hover &': {
                                                                opacity: 1,
                                                            },
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const productNameSlug = selectedProduct?.name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/__+/g, '_') || 'generated_image';
                                                            const filename = `${productNameSlug}_gen_${generationId}_img_${idx + 1}.jpg`;
                                                            downloadImage(img.url, filename);
                                                        }}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                        {img.status === 'pending' && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <CircularProgress />
                                            </Box>
                                        )}
                                        {img.status === 'failed' && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'error.main',
                                                    p: 2,
                                                    textAlign: 'center',
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    Image generation failed
                                                    <br />
                                                    {img.error}
                                                </Typography>
                                            </Box>
                                        )}
                                        {img.status !== 'completed' && (
                                            <Chip
                                                label={img.status.replace('_', ' ').toUpperCase()}
                                                size="small"
                                                color={img.status === 'failed' ? 'error' : 'info'}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    left: 8,
                                                    zIndex: 1,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* ✅ ДОБАВЛЕНИЕ РАЗДЕЛА DEBUG */}
            {(aiRawPrompt || aiRawResponse) && (
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Debug Info (Raw AI Data)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="overline">Raw Prompt sent to LLM</Typography>
                        <Paper variant="outlined" sx={{ p: 1, mb: 2, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {aiRawPrompt || 'Not available.'}
                        </Paper>
                        <Typography variant="overline">Raw Response from LLM</Typography>
                        <Paper variant="outlined" sx={{ p: 1, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {aiRawResponse || 'Not available.'}
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            )}

            <ImageGenerationHistoryDialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                historyGenerations={historyGenerations}
                product={selectedProduct}
                onLoadGeneration={handleHistoryLoad}
                isLoading={isLoadingHistory}
            />

            <Dialog open={!!fullscreenImageUrl} onClose={closeFullscreenImage} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Image Preview
                    <IconButton
                        aria-label="close"
                        onClick={closeFullscreenImage}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                    {fullscreenImageUrl && (
                        <img
                            src={fullscreenImageUrl}
                            alt="Full Screen"
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(100vh - 120px)',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <AIGuideDialog
                open={isAIGuideOpen}
                onClose={() => setIsAIGuideOpen(false)}
                aiService={viewedAIService}
            />

            <PromptViewerDialog
                open={isPromptViewerOpen}
                onClose={() => setIsPromptViewerOpen(false)}
                prompt={viewedPromptTemplate}
            />
        </Box>
    );
};

export default ImageTestPage;